import GuestI from '../interfaces/Guest';
import AllAccessGuestI from '../interfaces/AllAccessGuest';
import { GuestFilters } from '../interfaces/Filters';
import getAge from './getAge';

const SEARCH_FIELDS = [
    'guestID',
    'firstName',
    'middleName',
    'lastName',
    'stageName',
    'email',
    'phoneNumber',
    'licenseID',
    // ── tx-snapshot names attached by the backend for purged guests ──
    // (see guests.service.ts#annotateWithTxSnapshots). Lets the
    // existing haystack match real pre-purge names without restoring
    // any data.
    'txFirstName',
    'txLastName',
    'txStageName',
];

const matchesQuery = (guest: GuestI, query: string) => {
    const terms = query
        .toLowerCase()
        .split(/\s+/)
        .filter((t) => t.length > 0);

    if (terms.length === 0) return true;

    const haystack = SEARCH_FIELDS.map((key) => {
        const value = (guest as any)[key];
        return value === undefined || value === null ? '' : String(value);
    })
        .join(' ')
        .toLowerCase();

    return terms.every((term) => haystack.includes(term));
};

const matchesAge = (guest: GuestI, min: string, max: string) => {
    if (min === '' && max === '') return true;
    if (!guest.dateOfBirth) return false;

    const age = getAge(guest.dateOfBirth as Date);
    const minN = min === '' ? -Infinity : parseInt(min, 10);
    const maxN = max === '' ? Infinity : parseInt(max, 10);

    return age >= minN && age <= maxN;
};

const matchesDateRange = (guest: GuestI, from: string, to: string) => {
    if (from === '' && to === '') return true;
    if (!guest.entryDates || guest.entryDates.length === 0) return false;

    const recent = new Date(
        guest.entryDates[guest.entryDates.length - 1]
    ).getTime();

    const fromMs = from === '' ? -Infinity : new Date(from).getTime();
    // +1 day - 1ms so the end date is inclusive through 23:59:59
    const toMs =
        to === '' ? Infinity : new Date(to).getTime() + 86400000 - 1;

    return recent >= fromMs && recent <= toMs;
};

const matchesStanding = (guest: GuestI, standing: string) =>
    standing === '' || guest.standing === standing;

const matchesTos = (guest: GuestI, tos: string) => {
    if (tos === '') return true;
    const accepted = Boolean(guest.tos && guest.lease);
    return tos === 'yes' ? accepted : !accepted;
};

const getFilteredGuests = (
    guests: GuestI[] | AllAccessGuestI[],
    filters: GuestFilters
): GuestI[] => {
    return (guests as GuestI[]).filter(
        (guest) =>
            matchesQuery(guest, filters.query) &&
            matchesAge(guest, filters.ageMin, filters.ageMax) &&
            matchesDateRange(guest, filters.dateFrom, filters.dateTo) &&
            matchesStanding(guest, filters.standing) &&
            matchesTos(guest, filters.tos)
    );
};

export default getFilteredGuests;
